import { IAttribute, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { SapOdpDataset } from 'app/model/dataset/sap-odp.dataset';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

import { TabularCopySource } from './tabular-type.copysource';
import {
    AdapterContext,
    Constants,
    format,
    IValidationContext,
    ModelUtilties,
    stringIsNullOrEmpty
} from 'shell-models-lib';
import { IQueryAPIColumn } from 'app/apis/query-api.models';
import type { CopyActivity } from 'app/model/pipeline/copy.activity';
import type { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export enum SapSelectionSign {
    Inclusive = 'I',
    Exclusive = 'E'
}

export enum SapSelectionOption {
    EQ = 'EQ',
    CP = 'CP',
    BT = 'BT'
}

export interface ISapSelection {
    fieldName: string;
    sign: 'I' | 'E';
    option: 'EQ' | 'CP' | 'BT';
    low: string;
    high?: string;
}

export interface ISapColumn extends IQueryAPIColumn {
    SelectionMandatory?: boolean;
    SelectionEqual?: boolean;
    SelectionBetween?: boolean;
    SelectionPattern?: boolean;
    IsKey?: boolean;
}

@Metamodel.model({
    name: adfModelTypeMap.sapOdp.copySourceType,
    display: [],
    tags: {
        datasetModel: [
            Metamodel.modelFor(SapOdpDataset)
        ]
    }
})
export class SapOdpCopySource extends TabularCopySource {
    public static readonly EXTRACTION_MODE: IAttribute;
    public static readonly SUBSCRIBER_PROCESS: IAttribute;
    public static readonly PROJECTION: IAttribute;
    public static readonly SELECTION: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'EXTRACTION_MODE',
        display: stringResources.Extraction_Mode
    })
    public extractionMode?: 'Full' | 'Delta' | 'Recovery' = 'Full';

    @Metamodel.attribute({
        templatable: true,
        association: 'SUBSCRIBER_PROCESS',
        display: stringResources.SubscriberProcess,
        constraint: {
            validator: SapOdpCopySource.validateSapOdpSubscriberProcess
        }
    })
    public subscriberProcess?: string;

    @Metamodel.attribute({
        display: stringResources.DataFlowProjectionPanel_Projection,
        templatable: true,
        association: 'PROJECTION',
        accepts: ['Array'],
        constraint: {
            validator: SapOdpCopySource.validateSapOdpProjection
        }
    })
    public projection: string[] = [];

    @Metamodel.attribute({
        display: stringResources.Selection,
        templatable: true,
        association: 'SELECTION',
        accepts: ['Array'],
        constraint: {
            validator: SapOdpCopySource.validateSapOdpSelection
        }
    })
    public selection: ISapSelection[] = [];

    public schemaColumns: ISapColumn[] = [];

    // `subscriberName` is mandatory when `extractionMode` is `Delta` or `Recovery`
    public static validateSapOdpSubscriberProcess(copySource?: SapOdpCopySource): IViolation[] {
        const violations: IViolation[] = [];
        if (
            (
                copySource?.extractionMode === 'Delta'
                || copySource?.extractionMode === 'Recovery'
            )
            && stringIsNullOrEmpty(copySource.subscriberProcess)
        ) {
            violations.push({
                message: () => format(stringResources.RequiredTemplateWhen, stringResources.SubscriberProcess, stringResources.Extraction_Mode, copySource.extractionMode)
            });
        }
        if (!Constants.sapCdcSubscriberNameAndProcessPattern.test(copySource?.subscriberProcess)) {
            violations.push({
                message: () => format(stringResources.Validation_SapCDCSubscriberNameAndProcess, stringResources.SubscriberProcess)
            });
        }
        return violations;
    }

    public static validateSapOdpProjection(copySource?: SapOdpCopySource): IViolation[] {
        if (copySource?.projection?.some((value) => stringIsNullOrEmpty(value))) {
            return [{
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.FieldName, stringResources.SapOdp_DisplayText, stringResources.DataFlowProjectionPanel_Projection)
            }];
        }
        return [];
    }

    public static async validateSapOdpSelection(copySource?: SapOdpCopySource, value?: ISapSelection[], violation?: IViolation, context?: IValidationContext): Promise<IViolation[]> {
        const violations: IViolation[] = [];
        if (Array.isArray(copySource.selection) && copySource.selection.length) {
            if (!copySource.schemaColumns.length) {
                // Sometimes selectionColumns is set by sap-selection component. If not, fetch it here.
                const copyActivity: CopyActivity = copySource?.owner as CopyActivity;
                const sourceDataset = copyActivity?.sourceDataset;
                const datasetResource = await ModelUtilties.toResource(AdapterContext.defaultContextOf(context.contextData), sourceDataset);
                const apiService = await getServiceInstance<QueryApiFactoryService>(context.contextData, 'QueryApiFactoryService');

                try {
                    copySource.schemaColumns = await apiService.getMadridQueryApi().getSchemaAsync(
                        sourceDataset.linkedService,
                        datasetResource
                    ).then((schema) => schema.tabularSchema.map((value) => value.raw as ISapColumn));
                } catch {
                    /*
                        Columns contain information that validates required, but if the API fails, we shouldn't block the
                        customers to publish this copy activity.
                     */
                }
            }
            if (copySource.schemaColumns.length) {
                // Some fields are required
                const fieldNames = copySource.selection.map((v) => v.fieldName);
                const missingColumns: string[] = [];
                copySource.schemaColumns.forEach(({ SelectionMandatory, physicalName }) => {
                    if (SelectionMandatory && !fieldNames.includes(physicalName)) {
                        missingColumns.push(physicalName);
                    }
                });
                if (missingColumns.length) {
                    violations.push({
                        message: () => sanitizeAndFormatString(stringResources.RequiredValidatorMessageNoVowel, missingColumns.join(', '), stringResources.SapOdp_DisplayText, stringResources.Selection)
                    });
                }
                const columnMap = new Map(copySource.schemaColumns.map((column) => [column.physicalName, column]));
                copySource.selection.forEach(({ fieldName, option }) => {
                    const column = columnMap.get(fieldName);
                    if (column) {
                        const { SelectionEqual: selectionEqual, SelectionPattern: selectionPattern, SelectionBetween: selectionBetween } = column;
                        if (option === SapSelectionOption.EQ && !selectionEqual) {
                            violations.push({
                                message: () => sanitizeAndFormatString(stringResources.SapCdc_Copy_Selection_Option_Error, fieldName, SapSelectionOption.EQ, stringResources.SapOdp_DisplayText, stringResources.Selection)
                            });
                        }
                        if (option === SapSelectionOption.CP && !selectionPattern) {
                            violations.push({
                                message: () => sanitizeAndFormatString(stringResources.SapCdc_Copy_Selection_Option_Error, fieldName, SapSelectionOption.CP, stringResources.SapOdp_DisplayText, stringResources.Selection)
                            });
                        }
                        if (option === SapSelectionOption.BT && !selectionBetween) {
                            violations.push({
                                message: () => sanitizeAndFormatString(stringResources.SapCdc_Copy_Selection_Option_Error, fieldName, SapSelectionOption.BT, stringResources.SapOdp_DisplayText, stringResources.Selection)
                            });
                        }
                    }
                });
            }
        }
        violations.forEach((violation) => violation.doNotAutomaticallyCorrect = true);
        return violations;
    }
}
